/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';
import deepcopy from 'deepcopy';
import React from 'react';

import type { GridNormalCellConfig } from '@veeam/components/lib/GridCore/renderers/gridNormalCell';
import type { PropsWithChildren } from 'react';
import type { GridColumnMovingPluginConfig } from '@veeam/components/lib/GridCore/plugins/columnMoving';

import { ColumnResizeData, ColumnToggleData } from '../../../interfaces';
import { ColumnTexts } from '../../../../../enums';

import type { ColumnOrderProps } from '../../../interfaces/column-order-props';
import type { ColumnResizeProps, ColumnToggleProps, GridColumnProps, GridProps, GridStorageActions, GridColumn } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class ColumnsStore<RowDataT, TotalRowDataT, FilterT> {
    @observable.shallow columns: GridColumn<RowDataT>[];

    resizable: boolean;
    toggleable: boolean;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>,
        private gridStorageActions: GridStorageActions<FilterT> | null
    ) {
        makeObservable(this);

        this.resizable = props.resizable ?? true;
        this.toggleable = props.toggleable ?? true;

        const columnsStorageData = this.gridStorageActions?.getColumnsData();

        const columns: GridColumnProps<RowDataT>[] = deepcopy(props.columns)
            .map(column => ({
                ...column,
                cell: this.decorateCell(column),
                minWidth: column.minWidth ?? props.defaultColumnMinWidth,
                ...columnsStorageData?.[column.id],
            }));

        this.checkIfIdsAreUnique(columns);

        this.setColumns(columns);
    }

    private decorateCell(column: GridColumnProps<RowDataT>): GridNormalCellConfig<RowDataT>['value'] {
        return (rowData: RowDataT) => {
            let cellContent = column.cell(rowData);
            const defaultValue = column.defaultValue ?? ColumnTexts.DashSymbol;

            if (
                React.isValidElement(cellContent) &&
                cellContent.type === React.Fragment &&
                (
                    typeof (cellContent.props as PropsWithChildren)?.children === 'string' ||
                    typeof (cellContent.props as PropsWithChildren)?.children === 'number' ||
                    (cellContent.props as PropsWithChildren)?.children === undefined ||
                    (cellContent.props as PropsWithChildren)?.children === null
                )
            ) {
                cellContent = (cellContent.props as PropsWithChildren).children as (string | number | undefined | null);
            }

            if (cellContent === ColumnTexts.DashSymbol && column.defaultValue) {
                cellContent = column.defaultValue;
            }

            return cellContent !== undefined && cellContent !== null && cellContent !== '' ? cellContent : defaultValue;
        };
    }

    @action.bound
    onColumnResize({ id, width }: ColumnResizeData): void {
        const newColumns = this.columns.map((column) => {
            if (id === column.id) {
                column.width = width;
            }

            return column;
        });

        this.onColumnChanges(
            newColumns.map(column => ({ columnId: column.id, width: column.width }))
        );

        this.setColumns(newColumns);
    }

    @action.bound
    onColumnToggle({ id, isShow }: ColumnToggleData): void {
        const newColumns = this.columns.map((column) => {
            if (id === column.id) {
                column.hidden = !isShow;
            }

            return column;
        });

        this.onColumnChanges(
            newColumns.map(column => ({ columnId: column.id, hidden: column.hidden }))
        );

        this.setColumns(newColumns);
    }

    @action.bound
    onColumnOrder({ orders }: Parameters<GridColumnMovingPluginConfig<string>['onChange']>[0]): void {
        const newColumns = this.columns
            .map((column) => {
                const newOrder = orders[column.id];

                if (Number.isInteger(newOrder)) {
                    column.order = newOrder;
                }

                return column;
            });

        this.onColumnChanges(
            newColumns.map(column => ({ columnId: column.id, order: column.order }))
        );

        this.setColumns(newColumns);
    }

    onColumnChanges(columns: Array<ColumnToggleProps | ColumnResizeProps | ColumnOrderProps>) {
        this.gridStorageActions?.onColumnChanges(columns);
    }

    @action.bound
    setColumns(columns: GridColumnProps<RowDataT>[]): void {
        this.columns = columns;
    }

    checkIfIdsAreUnique(columns: GridColumnProps<RowDataT>[]): void {
        const uniqueIds = [...new Set(columns.map(column => column.id))];

        if (uniqueIds.length !== columns.length) {
            throw Error('Column\'s id should be unique.');
        }
    }
}
